import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { systemNotificationInternalSend } from "../../../../store/notification/action";
import { isEmpty } from "lodash";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SettingsPanel from "../../components/SettingsPanel";

// Default company 4 and staff id 11
const initialState = {
  companyId: "4",
  singleUserOnly: true,
  userId: "11",
  messageTypeId: 1,
  title: "",
  body: "",
  showNotification: true,
  priority: "normal", // low | normal | high
  actionUrl: "",
  channelInApp: true,
  channelPush: false,
  scheduleMode: "now", // now | schedule
  scheduleAt: "",
};

const toPayload = (f) => [
  {
    Company_Id: f.companyId,
    User_Id: f.singleUserOnly ? f.userId || null : null,
    MessageType_Id: f.messageTypeId,
    MessageTitle: f.title,
    MessageBody: f.body,
    FlagSingleUserOnly: f.singleUserOnly ? 1 : 0,
    FlagShowNotification: f.showNotification ? 1 : 0,
    MessageData: {
      priority: f.priority,
      action_url: f.actionUrl || undefined,
      client_request_id:
        crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString(),
    },
  },
];

export default function NotificationCenter() {
  const [form, setForm] = React.useState(initialState);
  const [preview, setPreview] = React.useState("inapp"); // inapp | push | email
  const dispatch = useDispatch();
  const awaitingRef = useRef(null);

  const update = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const canSend = useMemo(() => {
    if (form.scheduleMode === "schedule" && !form.scheduleAt) return false;
    if (!form.title?.trim() || !form.body?.trim()) return false;
    if (!form.channelInApp && !form.channelPush) return false;
    if (form.singleUserOnly && !form.userId?.trim()) return false;
    return true;
  }, [form]);

  const handleSendNow = () => {
    if (!canSend) {
      toast.warn("Please complete the required fields.");
      return;
    }
    const payload = toPayload(form);
    const clientId = payload[0]?.MessageData?.client_request_id;
    awaitingRef.current = { clientId };
    console.log("Outbound body:", JSON.stringify(payload, null, 2));
    dispatch(systemNotificationInternalSend(payload));
  };

  const {
    systemNotificationInternalSendAPI,
    systemNotificationInternalSendLoading,
    systemNotificationInternalSendError,
  } = useSelector((state) => ({
    systemNotificationInternalSendAPI:
      state.Notification.systemNotificationInternalSend,
    systemNotificationInternalSendLoading:
      state.Notification.systemNotificationInternalSendLoading,
    systemNotificationInternalSendError:
      state.Notification.systemNotificationInternalSendError,
  }));

  useEffect(() => {
    if (!awaitingRef.current) return;
    if (systemNotificationInternalSendLoading) return;

    if (systemNotificationInternalSendError) {
      toast.error("Failed to send the message.");
      awaitingRef.current = null;
      return;
    }
    if (isEmpty(systemNotificationInternalSendAPI)) return;

    toast.success(
      systemNotificationInternalSendAPI.message || "Message sent successfully!"
    );
    setForm((s) => ({
      ...s,
      title: "",
      body: "",
      actionUrl: "",
      scheduleAt: "",
    }));
    awaitingRef.current = null;
  }, [
    systemNotificationInternalSendAPI,
    systemNotificationInternalSendLoading,
    systemNotificationInternalSendError,
  ]);

  return (
    <SettingsPanel
      title="Notification Center"
      description="Compose and broadcast in-app and push notifications."
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
      <Grid container spacing={3}>
        {/* Breadcrumbs */}
        <Grid item xs={12}>
          <Breadcrumbs>
            <Link underline="hover" color="inherit">
              Super Admin
            </Link>
            <Link underline="hover" color="inherit">
              Notification Center
            </Link>
            <Typography color="text.primary">New Broadcast</Typography>
          </Breadcrumbs>
        </Grid>

        {/* Compose */}
        <Grid item md={7} xs={12}>
          <Card>
            <CardHeader title="Compose broadcast" />
            <CardContent>
              <Stack spacing={3}>
                {/* Company / Targeting */}
                <Stack spacing={2}>
                  <TextField
                    label="Company Id"
                    value={form.companyId}
                    onChange={(e) => update("companyId", e.target.value)}
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.singleUserOnly}
                        onChange={(e) =>
                          update("singleUserOnly", e.target.checked)
                        }
                      />
                    }
                    label="Single user only"
                  />
                  {form.singleUserOnly && (
                    <TextField
                      label="User Id"
                      value={form.userId}
                      onChange={(e) => update("userId", e.target.value)}
                      fullWidth
                    />
                  )}
                </Stack>

                {/* Message type */}
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Message type</Typography>
                  <ToggleButtonGroup
                    value={form.messageTypeId}
                    exclusive
                    onChange={(_, val) =>
                      val && update("messageTypeId", Number(val))
                    }
                    size="small"
                  >
                    <ToggleButton value={1}>Info</ToggleButton>
                    <ToggleButton value={2}>Success</ToggleButton>
                    <ToggleButton value={3}>Warning</ToggleButton>
                    <ToggleButton value={4}>Critical</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                {/* Title / Body */}
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Message"
                  multiline
                  minRows={4}
                  value={form.body}
                  onChange={(e) => update("body", e.target.value)}
                  fullWidth
                />

                {/* Channels */}
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.channelInApp}
                        onChange={(e) =>
                          update("channelInApp", e.target.checked)
                        }
                      />
                    }
                    label="In-app"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.channelPush}
                        onChange={(e) =>
                          update("channelPush", e.target.checked)
                        }
                      />
                    }
                    label="Push (FCM)"
                  />
                </Stack>

                <Divider />

                {/* Extras */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl fullWidth>
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                      Priority
                    </Typography>
                    <Select
                      value={form.priority}
                      onChange={(e) => update("priority", e.target.value)}
                      size="small"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Action URL"
                    placeholder="https://…"
                    value={form.actionUrl}
                    onChange={(e) => update("actionUrl", e.target.value)}
                    fullWidth
                  />
                </Stack>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.showNotification}
                      onChange={(e) =>
                        update("showNotification", e.target.checked)
                      }
                    />
                  }
                  label="Show as notification"
                />

                {/* Tags (placeholder) */}
                <Stack direction="row" spacing={1}>
                  <Chip label="maintenance" size="small" />
                  <Chip label="system" size="small" />
                </Stack>

                <Divider />

                {/* Schedule */}
                <FormControl>
                  <RadioGroup
                    row
                    value={form.scheduleMode}
                    onChange={(e) => update("scheduleMode", e.target.value)}
                  >
                    <FormControlLabel
                      value="now"
                      control={<Radio />}
                      label="Send now"
                    />
                    <FormControlLabel
                      value="schedule"
                      control={<Radio />}
                      label="Schedule"
                    />
                  </RadioGroup>
                </FormControl>
                {form.scheduleMode === "schedule" && (
                  <TextField
                    label="Date & time"
                    type="datetime-local"
                    value={form.scheduleAt}
                    onChange={(e) => update("scheduleAt", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button variant="outlined">Save draft</Button>
              <Button
                variant="contained"
                onClick={handleSendNow}
                disabled={!canSend || systemNotificationInternalSendLoading}
              >
                {systemNotificationInternalSendLoading
                  ? "Sending…"
                  : "Send now"}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item md={5} xs={12}>
          <Card>
            <CardHeader
              title="Preview"
              action={
                <ToggleButtonGroup
                  size="small"
                  value={preview}
                  exclusive
                  onChange={(_, v) => v && setPreview(v)}
                >
                  <ToggleButton value="inapp">In-app</ToggleButton>
                  <ToggleButton value="push">Push</ToggleButton>
                  <ToggleButton value="email">Email</ToggleButton>
                </ToggleButtonGroup>
              }
            />
            <CardContent>
              {preview === "inapp" && (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {form.title || "Notification title"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.body || "Message body…"}
                  </Typography>
                  {form.actionUrl && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="Open" size="small" />
                    </Box>
                  )}
                </Box>
              )}
              {preview === "push" && (
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Mobile push (approx.)
                  </Typography>
                  <Typography variant="subtitle2">
                    {form.title || "Title"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.body || "Body…"}
                  </Typography>
                </Box>
              )}
              {preview === "email" && (
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Email preview (placeholder)
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {form.title || "Subject"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.body || "Message…"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Container>
    </SettingsPanel>
  );
}
